import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import React, { useState, Suspense, useCallback, useEffect, Fragment } from "react";
import { Card, Dialog, Tooltip, Typography } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { OnClickAnchor } from "../../parts/OnClickAnchor";
import SuspenseImage from "../../parts/SuspenseImage";
import { Loading } from "../../parts/Loading";
import { useRecoilRefresher_UNSTABLE, useRecoilValue } from "recoil";
import { timeZoneSelector } from "../../store/TimeZoneState";
import { formatTimeZoneDate } from "../../utils/FPEUtils";
import { RC_INFORMATION_PANEL, rcDailyState, rcInformationVisibleState } from "../../store/RcInformationState";
import { useDisplayOperationRecoilState } from "../../hooks/useDisplayOperation";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 1000
  },
  rcInfo: {
    minWidth: 300,
    minHeight: 50,
  },
  rcValid: {
    textAlign: "left"
  },
  rcTitle: {

  },
  rcBody: {
    maxHeight: '80vh',
    overflow: 'auto',
    '&>pre': {
      textAlign: 'left',
      padding: '5px 3px'
    }
  },
  rcThumb: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    columnGap: 15
  },
  thumbnail: {
    height: 282,
    cursor: 'zoom-in'
  },
  previewWrapper: {
    height: 934,
    width: 660,
    background: '#fff'
  }

}));

const RcInformationPanel = () => {
  const [rcVisible, setRcVisible] = useDisplayOperationRecoilState(`${RC_INFORMATION_PANEL}.visible`, rcInformationVisibleState);

  const classes = useStyles();

  const handleClose = useCallback(() => {
    setRcVisible(false)
  }, [setRcVisible]);

  return (
    rcVisible &&
    <div className={classes.root}>
      <div className={clsx("widget-box", "dark-color")}>
        <div className={clsx("widget-header")}>
          <h4>SKY RC Daily</h4>
          <div className="widget-toolbar">
            <OnClickAnchor onClick={handleClose}>
              <Tooltip title="Close" arrow>
                <CloseIcon/>
              </Tooltip>
            </OnClickAnchor>
          </div>
        </div>
        <div className={clsx("widget-body", classes.rcInfo)}>
          <Suspense fallback={<Loading/>}>
            <RcInformation/>
          </Suspense>
        </div>
      </div>
    </div>
  );
};

const RcInformation = () => {
  const rcDaily = useRecoilValue(rcDailyState);
  const refresh = useRecoilRefresher_UNSTABLE(rcDailyState);
  const [previewSrc, setPreviewSrc] = useState(null);
  const timeZone = useRecoilValue(timeZoneSelector);

  const {t} = useTranslation();
  const classes = useStyles();

  useEffect(() => {
    return () => refresh();
    // eslint-disable-next-line
  }, []);

  const handlePreview = (src) => {
    setPreviewSrc(src);
  };

  const handlePreviewClose = () => {
    setPreviewSrc(null);
  }

  if (rcDaily === null || rcDaily === undefined) {
    return <Typography variant="h6" gutterBottom>{t('No valid RC Daily')}</Typography>
  }

  const validStr = `${formatTimeZoneDate(rcDaily.announced_time, timeZone)} (${t('rc_valid', {valid: formatTimeZoneDate(rcDaily.valid_time, timeZone)})})`;
  const titleStr = rcDaily.title;
  const bodyStr = rcDaily.body.replace(/\r\n/g, "\n");
  const thumbKeyList = Object.keys(rcDaily).reduce((accumulator, k) => {
    const m = k.match(/^image([0-9]*)_thumbnail/);
    if (m) {
      accumulator.push({
        idx: +m[1],
        key: m[0]
      })
    }
    return accumulator;
  }, []).sort((a, b) => a.idx - b.idx);

  return (
    <>
      <div className={classes.rcValid}>
        <Typography variant="caption" gutterBottom>{validStr}</Typography>
      </div>
      <div className={classes.rcTitle}>
        <Typography variant="h6" gutterBottom>{titleStr}</Typography>
      </div>
      <Card className={classes.rcBody} raised={true}>
        <pre>{bodyStr}</pre>
        <div className={classes.rcThumb}>
          {thumbKeyList.map(thumb => (
            <Fragment key={thumb.key} >
              <Card>
                <img src={rcDaily[thumb.key]} className={classes.thumbnail} loading="lazy" alt="" onClick={()=>handlePreview(rcDaily[`image${thumb.idx}`])}/>
              </Card>
            </Fragment>
          ))}
        </div>
      </Card>
      <Dialog open={previewSrc !== null} onClose={handlePreviewClose} maxWidth={false}>
        <div className={classes.previewWrapper}>
          <Suspense fallback={<Loading/>}>
            <SuspenseImage src={previewSrc} alt="" onClick={handlePreviewClose}/>
          </Suspense>
        </div>
      </Dialog>
    </>
  )
};

export default RcInformationPanel;